import {
  VisionProposalT,
  VisionLiteProposalT,
  SoloProposalT,
  LightreachProposalT,
  FundingProposalT,
  VisionDesignT,
} from "rdx/modules/projects/types/projects/post-sales";
import { ProposalStatusType } from "generated/vision-graphql";
import { isVisionProposal } from "rdx/modules/projects/types/projects/post-sales/guards";
import { formatDateTimeLong } from "lib/helpers";
import { buildVisionProposalUrl, isBatteryOnlyVisionProposal } from "util/visionProposalUrl";

export type ProposalUnionT =
  | VisionProposalT
  | VisionLiteProposalT
  | SoloProposalT
  | LightreachProposalT
  | FundingProposalT;

export type StatusKeyT = "rejected" | "in_progress" | "completed" | "contract_signed";

export const determineStatusKey = (proposal: ProposalUnionT): StatusKeyT => {
  if (isVisionProposal(proposal)) {
    const proposalStatus = proposal.meta?.status;
    if (proposalStatus === ProposalStatusType.contract_signed) return "contract_signed";

    const designs = proposal.meta?.designs ?? [];
    if (designs.length === 0) return "in_progress";

    const mostRecentDesign = [...designs].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    )[0];

    if (mostRecentDesign.designRequest?.rejectedAt) return "rejected";
    if (mostRecentDesign.completedAt) return "completed";

    // Aurora designs are synced in from Aurora rather than produced by Vision's
    // design request flow, so they occasionally land without a completedAt. When
    // the newest design is the proposal's active Aurora design, the design work
    // is done and the proposal is not awaiting a revision.
    if (proposal.meta?.auroraDesignId && mostRecentDesign.id === proposal.meta.auroraDesignId) {
      return "completed";
    }

    return "in_progress";
  }

  if (proposal.kind === "funding") {
    if (proposal.rejection?.rejectedAt || proposal.statusDisplay === "rejected") return "rejected";
    if (proposal.complete) return "completed";
    return "in_progress";
  }

  if (proposal.rejection?.rejectedAt) return "rejected";

  return "completed";
};

export const isBatteryProposal = (proposal: ProposalUnionT): boolean => {
  if (!isVisionProposal(proposal)) return false;

  return isBatteryOnlyVisionProposal(proposal.meta);
};

export const determineCardStatusKey = (proposal: ProposalUnionT): StatusKeyT => {
  const statusKey = determineStatusKey(proposal);

  if (isBatteryProposal(proposal) && statusKey === "in_progress") return "completed";

  return statusKey;
};

export const shouldShowStatusChip = (proposal: ProposalUnionT): boolean => {
  if (!isBatteryProposal(proposal)) return true;

  return determineCardStatusKey(proposal) === "contract_signed";
};

export const shouldShowAuroraTag = (proposal: ProposalUnionT, isAdmin: boolean): boolean => {
  return isAdmin && isVisionProposal(proposal) && !!proposal.meta?.auroraDesignId;
};

export const determineIfRejected = (proposal: ProposalUnionT): boolean => {
  return determineStatusKey(proposal) === "rejected";
};

export const determineIfInProgress = (proposal: ProposalUnionT): boolean => {
  return determineStatusKey(proposal) === "in_progress";
};

export const hasOpenVisionDesignRequest = (proposal: ProposalUnionT): boolean => {
  if (!isVisionProposal(proposal)) return false;

  return (proposal.meta?.designs ?? []).some(
    (design) => !!design.designRequest && !design.designRequest.completedAt && !design.designRequest.rejectedAt,
  );
};

export const formatRejectionDetails = (proposal: ProposalUnionT): string | null => {
  if (isVisionProposal(proposal)) return null;

  if (proposal.kind === "funding") {
    if (!proposal.rejection?.rejectedAt && proposal.statusDisplay !== "rejected") return null;
    return proposal.adminNotes || "No details provided";
  }

  if (!proposal.rejection?.rejectedAt) return null;
  return proposal.rejection.detailsToChange || "No details provided";
};

export const getVisionRevisionStatus = (design: VisionDesignT): string => {
  const { designRequest } = design;

  if (designRequest?.rejectedAt) {
    return `Rejected ${formatDateTimeLong(designRequest.rejectedAt)}`;
  }
  if (design.completedAt) {
    return `Completed ${formatDateTimeLong(design.completedAt)}`;
  }
  if (designRequest?.designUser?.id) {
    return "In Progress";
  }
  return designRequest
    ? `Requested ${formatDateTimeLong(designRequest.createdAt)}`
    : `Created ${formatDateTimeLong(design.createdAt)}`;
};

export const getProposalUrl = (proposal: ProposalUnionT): string | null | undefined => {
  if (isVisionProposal(proposal)) {
    return buildVisionProposalUrl(proposal.meta);
  }
  return proposal.url;
};

const UUID_PATTERN = "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}";

export const extractAuroraDesignId = (input: string): string | null => {
  const designMatch = input.match(new RegExp(`designs/(${UUID_PATTERN})`));
  if (designMatch) return designMatch[1];

  const uuidMatch = input.match(new RegExp(UUID_PATTERN));
  return uuidMatch ? uuidMatch[0] : null;
};
